/**
 * AppsCube — 3D куб-карусель приложений платформы.
 *
 * Шестигранная призма (CSS preserve-3d): каждая грань — телефон с живым
 * скриншотом production-приложения. Крутится пальцем/мышью (drag по X
 * с инерцией и снапом к грани), авто-листает раз в 5 секунд когда
 * пользователь не трогает. Активная грань подсвечена золотом, инфо-карточка
 * рядом обновляется под неё.
 *
 * Реализация без библиотек: rotation живёт в ref, transform пишется напрямую
 * в style внутри rAF (re-render только при смене активной грани).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Download } from "lucide-react";
import { useLang } from "../i18n";
import { useReveal, prefersReducedMotion } from "../lib/motion";
import { CUBE_APPS, TOTAL_APPS, shotUrl, type AppId } from "../content/apps";
import { SectionHead } from "./SectionHead";
import { CASHIER_DOWNLOAD_URL, DEMO_URL } from "../content/contacts";

const N = CUBE_APPS.length;
const STEP = 360 / N;
/** Половина ширины грани / tan(π/N) — радиус призмы */
const FACE_W = 264;
const FACE_H = 540;
const RADIUS = Math.round(FACE_W / 2 / Math.tan(Math.PI / N));
/** Габариты сцены куба (фикс. пиксели; на узких экранах масштабируются через .cube-fit) */
const STAGE_W = FACE_W * 1.7;
const STAGE_H = FACE_H + 60;

const AUTO_MS = 5200;
const SNAP_EASE = 0.12;

export function AppsCube() {
  const { t } = useLang();
  const sectionRef = useReveal<HTMLElement>();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const faceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);

  // --- Вся кинетика в refs, чтобы не дёргать React на каждый кадр ---
  const rot = useRef(0); // текущий угол
  const target = useRef(0); // целевой угол (снап/автоплей)
  const dragging = useRef(false);
  const lastX = useRef(0);
  const velocity = useRef(0);
  const lastInteract = useRef(0);
  const raf = useRef(0);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const apply = () => {
      body.style.transform = `translateZ(-${RADIUS}px) rotateY(${rot.current}deg)`;
      // Дим неактивных граней по углу до фронта
      for (let i = 0; i < N; i++) {
        const el = faceRefs.current[i];
        if (!el) continue;
        const faceAngle = ((i * STEP + rot.current) % 360 + 360) % 360;
        const dist = Math.min(faceAngle, 360 - faceAngle); // 0 = фронт
        const k = Math.max(0, 1 - dist / 110);
        el.style.opacity = String(0.25 + 0.75 * k);
        el.style.filter = `brightness(${0.55 + 0.45 * k})`;
      }
      const idx = ((Math.round(-rot.current / STEP) % N) + N) % N;
      setActive((prev) => (prev === idx ? prev : idx));
    };

    const tick = (now: number) => {
      if (!dragging.current) {
        // Инерция → затем плавный снап к target
        if (Math.abs(velocity.current) > 0.05) {
          rot.current += velocity.current;
          velocity.current *= 0.94;
          target.current = Math.round(rot.current / STEP) * STEP;
        } else {
          rot.current += (target.current - rot.current) * SNAP_EASE;
        }
        // Автоплей: листаем грань если давно не трогали
        if (
          !prefersReducedMotion &&
          now - lastInteract.current > AUTO_MS &&
          Math.abs(target.current - rot.current) < 0.5
        ) {
          target.current -= STEP;
          lastInteract.current = now;
        }
      }
      apply();
      raf.current = requestAnimationFrame(tick);
    };

    lastInteract.current = performance.now();
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  // --- Pointer drag ---
  /** Суммарный сдвиг за жест: если был реальный drag — глушим click по грани */
  const dragDistance = useRef(0);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    lastX.current = e.clientX;
    velocity.current = 0;
    dragDistance.current = 0;
    lastInteract.current = performance.now();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastX.current;
    lastX.current = e.clientX;
    dragDistance.current += Math.abs(dx);
    rot.current += dx * 0.28;
    velocity.current = dx * 0.28;
    lastInteract.current = performance.now();
  };
  const endDrag = () => {
    if (!dragging.current) return;
    dragging.current = false;
    target.current = Math.round(rot.current / STEP) * STEP;
    lastInteract.current = performance.now();
  };

  const go = (dir: 1 | -1) => {
    target.current = (Math.round(target.current / STEP) - dir) * STEP;
    velocity.current = 0;
    lastInteract.current = performance.now();
  };

  /** Клик по боковой грани — кратчайший доворот к ней */
  const goToFace = (i: number) => {
    if (dragDistance.current > 8) return; // это был drag, не клик
    if (i === active) return;
    let delta = ((i - active) % N + N) % N;
    if (delta > N / 2) delta -= N;
    target.current = (Math.round(target.current / STEP) - delta) * STEP;
    velocity.current = 0;
    lastInteract.current = performance.now();
  };

  const app = CUBE_APPS[active];
  const info = t.apps.items[app.id as AppId];

  return (
    <section id="apps" ref={sectionRef} className="relative overflow-hidden">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}>
        <SectionHead
          num="03"
          label={t.apps.label}
          title={t.apps.title}
          accent={t.apps.accent}
          aside={t.apps.aside}
        />

        <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
          {/* ---- Сцена с кубом (адаптивная обёртка масштабирует фикс-сцену под колонку) ---- */}
          <div
            className="cube-fit mx-auto"
            style={{ "--stage-w": `${STAGE_W}px`, aspectRatio: `${STAGE_W} / ${STAGE_H}` } as React.CSSProperties}
          >
          <div
            className="cube-stage reveal select-none"
            data-reveal=""
            style={{
              width: STAGE_W,
              height: STAGE_H,
              touchAction: "pan-y",
              cursor: "grab",
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={endDrag}
            role="region"
            aria-label={t.apps.label}
            aria-roledescription="carousel"
          >
            {/* Подиум-свечение под кубом */}
            <div
              className="absolute bottom-0 left-1/2 h-24 w-[120%] -translate-x-1/2"
              aria-hidden
              style={{
                background:
                  "radial-gradient(50% 90% at 50% 100%, rgba(212,166,86,0.16), transparent 70%)",
              }}
            />
            <div
              ref={bodyRef}
              className="cube-body mx-auto"
              style={{ width: FACE_W, height: FACE_H }}
            >
              {CUBE_APPS.map((a, i) => (
                <div
                  key={a.id}
                  ref={(el) => {
                    faceRefs.current[i] = el;
                  }}
                  className="cube-face"
                  style={{
                    transform: `rotateY(${i * STEP}deg) translateZ(${RADIUS}px)`,
                    cursor: i === active ? "grab" : "pointer",
                  }}
                  onClick={() => goToFace(i)}
                >
                  <div className="phone-frame h-full w-full">
                    {/* Плейсхолдер под скриншотом (виден пока файла нет) */}
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
                      style={{
                        background:
                          "linear-gradient(160deg, #17140c 0%, #0a0908 70%)",
                      }}
                    >
                      <span className="t-brand text-base" style={{ color: "var(--gold)" }}>
                        {String(a.index).padStart(2, "0")}
                      </span>
                      <span className="t-display text-xl" style={{ color: "var(--fg-soft)" }}>
                        {t.apps.items[a.id].name}
                      </span>
                    </div>
                    <img
                      src={shotUrl(a.shot)}
                      alt={t.apps.items[a.id].name}
                      loading="lazy"
                      draggable={false}
                      className="relative"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p
              className="absolute bottom-[-8px] left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.3em]"
              style={{ color: "var(--fg-muted)" }}
            >
              ← {t.apps.dragHint} →
            </p>
          </div>
          </div>

          {/* ---- Инфо-карточка активной грани ---- */}
          <div className="reveal" data-reveal="" style={{ "--reveal-delay": "150ms" } as React.CSSProperties}>
            <div className="mb-6 flex items-baseline gap-4">
              <span className="t-brand text-5xl" style={{ color: "var(--gold)" }}>
                {String(active + 1).padStart(2, "0")}
              </span>
              <span className="t-eyebrow">/ {String(N).padStart(2, "0")}</span>
            </div>

            <h3 className="t-display mb-2 text-3xl sm:text-4xl" style={{ color: "var(--fg)" }}>
              {info.name}
            </h3>
            <p className="t-serif-i mb-6 text-xl" style={{ color: "var(--gold-bright)" }}>
              {info.role}
            </p>
            <p className="mb-8 max-w-md text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>
              {info.desc}
            </p>

            <div className="mb-10 flex flex-wrap gap-2">
              {info.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                  style={{ borderColor: "var(--line-strong)", color: "var(--fg-soft)" }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => go(-1)}
                className="flex h-12 w-12 items-center justify-center rounded-full border transition-colors hover:border-[var(--gold-line)]"
                style={{ borderColor: "var(--line-strong)", color: "var(--fg)" }}
                aria-label="Предыдущее приложение"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                className="flex h-12 w-12 items-center justify-center rounded-full border transition-colors hover:border-[var(--gold-line)]"
                style={{ borderColor: "var(--line-strong)", color: "var(--fg)" }}
                aria-label="Следующее приложение"
              >
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
              {app.id === "guest" ? (
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost ml-2 !py-3 !px-6"
                >
                  {t.apps.openDemo}
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </a>
              ) : null}
              {/* Касса — единственное приложение, которое ставится на компьютер:
                  даём прямую загрузку свежего установщика (см. contacts.ts). */}
              {app.id === "cashier" ? (
                <a href={CASHIER_DOWNLOAD_URL} className="btn-ghost ml-2 !py-3 !px-6">
                  <Download className="h-4 w-4" aria-hidden />
                  {t.apps.downloadCashier}
                </a>
              ) : null}
            </div>

            <p className="mt-10 text-sm" style={{ color: "var(--fg-muted)" }}>
              {t.apps.totalNote.replace("{total}", String(TOTAL_APPS))}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
